import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const KinOrdersView = ({ kinId, isCoordinator }) => {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(null);

  useEffect(() => {
    if (kinId) {
      fetchOrders();
    }
  }, [kinId]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kin-orders/kin/${kinId}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setOrders(data.orders || []);
      } else {
        const err = await res.json();
        setError(err.error || 'Failed to load orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendToVendor = async (orderId) => {
    if (!window.confirm('Send this order to the vendor?')) return;
    
    setSending(orderId);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/kin-orders/order/${orderId}/send-to-vendor`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (res.ok) {
        alert('✅ Order sent to vendor successfully!');
        fetchOrders();
      } else {
        const err = await res.json();
        alert('❌ Failed to send order: ' + (err.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error sending order:', err);
      alert('❌ Failed to send order');
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return <div className="text-gray-500">Loading orders...</div>;
  }

  if (error) {
    return <div className="text-red-600">Error: {error}</div>;
  }

  if (orders.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4">📦 Vendor Orders</h3>
        <p className="text-gray-500">No vendor orders have been generated yet.</p>
        <p className="text-sm text-gray-400 mt-2">After finalizing drafts, use "Generate Vendor Orders" to create orders.</p>
      </div>
    );
  }

  const totalAmount = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">📦 Vendor Orders ({orders.length})</h3>
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-center">
          <p className="text-xs text-green-600">Total Order Value</p>
          <p className="text-lg font-bold text-green-700">${totalAmount.toFixed(2)}</p>
        </div>
      </div>

      <div className="space-y-4">
        {orders.map(order => {
          const vendorName = order.producer?.farmName || order.producer?.name || order.hubId?.name || 'Vendor';
          const orderDate = new Date(order.createdAt).toLocaleDateString();
          const statusColors = {
            pending: 'bg-yellow-100 text-yellow-800',
            sent_to_vendor: 'bg-blue-100 text-blue-800',
            claimed: 'bg-blue-100 text-blue-800',
            in_transit: 'bg-purple-100 text-purple-800',
            delivered: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800',
            refunded: 'bg-gray-100 text-gray-800',
            damaged: 'bg-orange-100 text-orange-800'
          };
          const statusLabel = {
            pending: '⏳ Pending',
            sent_to_vendor: '📤 Sent to Vendor',
            claimed: '📦 Claimed',
            in_transit: '🚚 In Transit',
            delivered: '✅ Delivered',
            cancelled: '❌ Cancelled',
            refunded: '↩️ Refunded',
            damaged: '⚠️ Damaged'
          };

          const isSentToVendor = order.status === 'sent_to_vendor' || order.metadata?.sentToVendorAt;

          return (
            <div key={order._id} className="border rounded-lg p-4 bg-white shadow-sm">
              <div className="flex flex-wrap justify-between items-start gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-800">{vendorName}</span>
                    <span className={`text-xs px-2 py-0.5 rounded ${statusColors[order.status] || 'bg-gray-100 text-gray-800'}`}>
                      {statusLabel[order.status] || order.status}
                    </span>
                    {isSentToVendor && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">
                        ✅ Sent to Vendor
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {order.products?.length || 0} items • Order #{order._id.toString().slice(-6)}
                  </p>
                  <p className="text-xs text-gray-400">
                    Created: {orderDate}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-lg font-bold text-green-600">${order.totalAmount?.toFixed(2) || '0.00'}</p>
                  {order.paymentStatus && (
                    <p className="text-xs text-gray-500">Payment: {order.paymentStatus}</p>
                  )}
                  {isCoordinator && order.status === 'pending' && !isSentToVendor && (
                    <button
                      onClick={() => handleSendToVendor(order._id)}
                      disabled={sending === order._id}
                      className="mt-2 text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 w-full sm:w-auto"
                    >
                      {sending === order._id ? 'Sending...' : '📤 Send to Vendor'}
                    </button>
                  )}
                  {isCoordinator && isSentToVendor && (
                    <span className="text-xs text-green-600 mt-2 block">✅ Sent</span>
                  )}
                </div>
              </div>

              {order.products && order.products.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <div className="flex flex-wrap gap-1">
                    {order.products.slice(0, 5).map((item, idx) => (
                      <span key={idx} className="bg-gray-100 px-2 py-0.5 rounded-full text-xs">
                        {item.name || 'Product'} ×{item.quantity}
                      </span>
                    ))}
                    {order.products.length > 5 && (
                      <span className="text-gray-400 text-xs">+{order.products.length - 5} more</span>
                    )}
                  </div>
                </div>
              )}

              {isCoordinator && order.metadata?.memberShares && (
                <div className="mt-2 pt-2 border-t border-gray-100">
                  <p className="text-xs text-gray-500">Member Shares:</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {Object.entries(order.metadata.memberShares).map(([memberId, share]) => (
                      <span key={memberId} className="text-xs bg-blue-50 px-2 py-0.5 rounded">
                        ${share.totalAmount?.toFixed(2) || '0.00'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KinOrdersView;
